import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, UsersThree, Clock } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

export default function Connections() {
  const [conns, setConns] = useState([]);

  const load = () => api.get("/connections").then((r) => setConns(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (id, status) => {
    await api.put(`/connections/${id}`, null, { params: { status } });
    toast.success(status === "accepted" ? "Connection accepted" : "Updated");
    load();
  };

  const incoming = conns.filter((c) => c.incoming && c.status === "pending");
  const accepted = conns.filter((c) => c.status === "accepted");
  const outgoing = conns.filter((c) => !c.incoming && c.status === "pending");

  const Row = ({ c, children }) => {
    const o = c.other || {};
    const initials = (o.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
    return (
      <div className="flex items-center gap-4 rounded-xl border border-border/60 p-4" data-testid="connection-row">
        <Avatar className="h-11 w-11"><AvatarImage src={fileUrl(o.avatar_file_id)} /><AvatarFallback className="bg-accent">{initials}</AvatarFallback></Avatar>
        <div className="min-w-0 flex-1">
          <Link to={`/u/${o.user_id}`} className="block truncate font-medium hover:underline">{o.name || "Alumnus"}</Link>
          <div className="truncate text-xs text-muted-foreground">{o.headline || c.category}</div>
        </div>
        {children}
      </div>
    );
  };

  return (
    <main className="mx-auto max-w-3xl px-5 py-8">
      <p className="overline text-secondary mb-1">Your network</p>
      <h1 className="font-heading text-3xl font-light tracking-tight sm:text-4xl">Connections</h1>

      <div className="mt-8 space-y-8">
        <section>
          <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-medium"><Clock className="text-secondary" size={18} /> Requests <Badge variant="secondary" className="rounded-full">{incoming.length}</Badge></h2>
          <div className="space-y-3">
            {incoming.length === 0 && <p className="text-sm text-muted-foreground">No pending requests.</p>}
            {incoming.map((c) => (
              <Row key={c.id} c={c}>
                <div className="flex gap-2">
                  <Button size="icon" className="h-9 w-9 rounded-full" onClick={() => act(c.id, "accepted")} data-testid="accept-connection"><Check size={16} /></Button>
                  <Button size="icon" variant="outline" className="h-9 w-9 rounded-full" onClick={() => act(c.id, "rejected")}><X size={16} /></Button>
                </div>
              </Row>
            ))}
          </div>
        </section>

        <section>
          <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-medium"><UsersThree className="text-secondary" size={18} /> Connected <Badge variant="secondary" className="rounded-full">{accepted.length}</Badge></h2>
          <div className="space-y-3">
            {accepted.length === 0 && <p className="text-sm text-muted-foreground">No connections yet. Explore the directory to connect.</p>}
            {accepted.map((c) => <Row key={c.id} c={c}><Badge variant="outline" className="rounded-full">{c.category}</Badge></Row>)}
          </div>
        </section>

        {outgoing.length > 0 && (
          <section>
            <h2 className="mb-3 font-heading text-lg font-medium">Sent requests</h2>
            <div className="space-y-3">
              {outgoing.map((c) => <Row key={c.id} c={c}><Badge variant="secondary" className="rounded-full">Pending</Badge></Row>)}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
